import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormField, FormHelp, Input, Label } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditWebApiHandler_contactName } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm } from '../../../../hooks';
import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import WizardServerError from '../../../../model/WizardServerError';

import { WizardContext } from '../../../../components/Wizard/context';
import WizardAlert from '../../../../components/WizardAlert';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';
import type { MockEventProps } from './interfaces';

const inputName = 'contactName';

const NameFormField = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { contactName, listingAlias } = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const { mutateAsync: updateContactName } = useRentalManagerUserPropertyEditWebApiHandler_contactName({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserPropertyEditWebApiHandler_contactName',
      },
      invalidates: ['users-properties'],
    },
  });

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      contactName,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error value: inputs.contactName,
          value: inputs.contactName,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement> | MockEventProps) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    return updateContactName({ getParams: { contactName: value, listingAlias } })
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

        return;
      })
      .catch((err) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? String(err)
              : 'Request failed, try again later';
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement> | MockEventProps) => {
    clearServerError();
    // @ts-expect-error handleFormChange(event);
    handleFormChange(event);
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField
        data-testid="NameFormField"
        label={<Label required={true}>Name</Label>}
        // @ts-expect-error errorObj.contactName
        error={!!errorObj.contactName}
        control={
          <Input
            size={isScreenDesktop ? 'lg' : 'md'}
            name={inputName}
            value={inputs.contactName as string}
            onChange={handleChange}
            onBlur={save}
            className="fs-mask"
          />
        }
        // @ts-expect-error errorObj.contactName
        description={<FormHelp>{errorObj.contactName}</FormHelp>}
      />
    </>
  );
};

export default NameFormField;
